import { useState } from 'react'
import { BroadcastDevice, BroadcastDeviceTemplate } from '@magnetar/data-access'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { InfoSetting } from './InfoSetting'
import { MarqueeSetting } from './MarqueeSetting'
import { TVThemeSetting } from './TVThemeSetting'
import { useBroadcastDeviceById } from '@/hooks/useBroadcastDeviceById'
import { BannerSetting } from './BannerSetting'
import { VodList } from './VodList'
import { BroadcastDeviceVodType } from '@/data/broadcastDeviceVodType'
import { PeopleFlowDeviceSetting } from './PeopleFlowDeviceSetting'
import IfAnyPermission from '@/components/IfAnyPermission'
import { AllowedAction } from '@/data/allowedActions'
import { Menu, X } from 'lucide-react'

// 電視影片 / 衛教影片 / 人流監控 這三個 tab 限 admin + provider only。
// clinic / clinic_group user 在裝置 dialog 內只能看到「基本資料 / 跑馬燈 /
// TV版型 / 輪播圖片」這四個 tab（基本資料唯讀，其餘可編輯）。
// 採用與 InfoSetting 編輯 gate、新增裝置鈕一致的 admin+provider 權限組。
const BROADCAST_DEVICE_VOD_PERMISSIONS = [
  { module: 'system_clinic', action: AllowedAction.R },
  { module: 'system_provider', action: AllowedAction.R },
  { module: 'provider_clinic_management', action: AllowedAction.R },
]

type SettingType =
  | 'info'
  | 'marquee'
  | 'tvTheme'
  | 'banner'
  | 'tv_vod'
  | 'health_vod'
  | 'people_flow_device'

type BroadcastDevicePageProps = {
  clinicId: number
  broadcastDeviceId?: number
  onMutate?: () => void
  onOpenChange?: (open: boolean) => void
}

// getBroadcastDeviceById 會 include tv_template 關聯，但基礎的 BroadcastDevice
// 型別不含此 relation，因此在前端補上對應的可選欄位。
type BroadcastDeviceWithTemplate = BroadcastDevice & {
  tv_template?: BroadcastDeviceTemplate | null
}

export default function BroadcastDevicePage(props: BroadcastDevicePageProps) {
  const [activeTab, setActiveTab] = useState<SettingType>('info')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { device, isLoading } = useBroadcastDeviceById(props.broadcastDeviceId)
  const deviceWithTemplate = device as BroadcastDeviceWithTemplate | undefined

  const handleTabChange = (tab: SettingType) => {
    setActiveTab(tab)
    // 在移動端選擇後自動關閉側邊欄
    setSidebarOpen(false)
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'info':
        return <InfoSetting device={device} isLoading={isLoading} onSuccess={props?.onMutate} />
      case 'marquee':
        return <MarqueeSetting broadcastDeviceId={props.broadcastDeviceId} />
      case 'tvTheme':
        return <TVThemeSetting broadcastDeviceId={props.broadcastDeviceId} />
      case 'banner':
        return <BannerSetting broadcastDeviceId={props.broadcastDeviceId} />
      case 'tv_vod':
        return (
          <VodList
            type={BroadcastDeviceVodType.tv}
            broadcastDeviceId={props.broadcastDeviceId}
            onSuccess={props?.onMutate}
            templateName={deviceWithTemplate?.tv_template?.template_name}
            templateId={deviceWithTemplate?.tv_template_id}
          />
        )
      case 'health_vod':
        return (
          <VodList
            type={BroadcastDeviceVodType.health_vod}
            broadcastDeviceId={props.broadcastDeviceId}
            onSuccess={props?.onMutate}
            templateName={deviceWithTemplate?.tv_template?.template_name}
            templateId={deviceWithTemplate?.tv_template_id}
          />
        )
      case 'people_flow_device':
        return (
          <PeopleFlowDeviceSetting
            clinicId={props.clinicId}
            broadcastDeviceId={props.broadcastDeviceId}
            onSuccess={props?.onMutate}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 md:px-6 py-4 border-b bg-card flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* 漢堡選單按鈕 - 只在移動端顯示 */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div>
              <h2 className="text-xl md:text-2xl font-bold tracking-tight">
                {isLoading ? '載入中...' : `廣播裝置設定 #${device?.broadcast_device_id || ''}`}
              </h2>
              <p className="text-sm text-muted-foreground mt-1 hidden sm:block">
                在這裡管理廣播裝置的基本資訊與相關設定
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex overflow-hidden relative" style={{ height: 'calc(90vh - 80px)' }}>
        {/* 移動端覆蓋層 */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div
          className={cn(
            'w-48 border-r bg-white flex-shrink-0 overflow-y-auto z-50',
            'md:relative md:translate-x-0',
            'fixed left-0 top-0 h-full transition-transform duration-300 ease-in-out',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="flex items-center justify-between p-4 border-b md:hidden">
            <span className="font-semibold">選單</span>
            <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <nav className="p-4 space-y-2">
            <Button
              variant={activeTab === 'info' ? 'secondary' : 'ghost'}
              className={cn('w-full justify-start')}
              onClick={() => handleTabChange('info')}
            >
              基本資料
            </Button>
            <Button
              variant={activeTab === 'marquee' ? 'secondary' : 'ghost'}
              className={cn('w-full justify-start')}
              onClick={() => handleTabChange('marquee')}
            >
              跑馬燈
            </Button>
            <Button
              variant={activeTab === 'tvTheme' ? 'secondary' : 'ghost'}
              className={cn('w-full justify-start')}
              onClick={() => handleTabChange('tvTheme')}
            >
              TV版型
            </Button>
            <Button
              variant={activeTab === 'banner' ? 'secondary' : 'ghost'}
              className={cn('w-full justify-start')}
              onClick={() => handleTabChange('banner')}
            >
              輪播圖片
            </Button>
            <IfAnyPermission permissions={BROADCAST_DEVICE_VOD_PERMISSIONS}>
              <Button
                variant={activeTab === 'tv_vod' ? 'secondary' : 'ghost'}
                className={cn('w-full justify-start')}
                onClick={() => handleTabChange('tv_vod')}
              >
                電視影片
              </Button>
            </IfAnyPermission>
            <IfAnyPermission permissions={BROADCAST_DEVICE_VOD_PERMISSIONS}>
              <Button
                variant={activeTab === 'health_vod' ? 'secondary' : 'ghost'}
                className={cn('w-full justify-start')}
                onClick={() => handleTabChange('health_vod')}
              >
                衛教影片
              </Button>
            </IfAnyPermission>
            <IfAnyPermission permissions={BROADCAST_DEVICE_VOD_PERMISSIONS}>
              <Button
                variant={activeTab === 'people_flow_device' ? 'secondary' : 'ghost'}
                className={cn('w-full justify-start')}
                onClick={() => handleTabChange('people_flow_device')}
              >
                人流監控
              </Button>
            </IfAnyPermission>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 pb-6">
          <div className="pt-5">{renderContent()}</div>
        </div>
      </div>
    </div>
  )
}
